import type { QuizAnswers } from "@/features/quiz/types"
import { parseBirthPlace } from "@/features/quiz/lib/birthplace"
import { getZodiacSlug } from "../lib/zodiac"
import type { Gender, MaritalStatus, UserProfileInput } from "../types"

const GENDERS: Gender[] = ["male", "female", "other"]
const MARITAL_STATUSES: MaritalStatus[] = ["free", "soulmate", "married", "difficult"]

/**
 * Last-resort display name when the quiz never asked for one (astro-map /
 * soulmate funnels) and the sign-in provider gave none (Apple withholds it on
 * repeat sign-ins). Keeps the profile creatable instead of silently skipped.
 */
export const FALLBACK_NAME = "Friend"

function asString(value: QuizAnswers[string] | null | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null
}

/** Convert the quiz time ("9:00 AM" / "12:30 PM" / "unknown") to 24h "HH:mm" or null. */
function normalizeBirthTime(value: string | null): string | null {
  if (!value || value === "unknown") return null
  const match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null
  let hour = Number(match[1]) % 12
  const minute = match[2]
  if (match[3].toUpperCase() === "PM") hour += 12
  return `${String(hour).padStart(2, "0")}:${minute}`
}

interface BuildProfileOptions {
  /** Account display name (Apple/Google) used when the quiz collected no name. */
  fallbackName?: string | null
}

/**
 * Build the backend profile from quiz answers.
 *
 * `name` is best-effort so the profile is always creatable: quiz answer, else
 * the account display name, else a placeholder. `gender` and `birth date` stay
 * required and return null when missing/invalid — they are astrology inputs we
 * cannot invent, and every real funnel collects them (so null never happens in
 * practice). The caller skips the upsert on null.
 */
export function buildUserProfile(
  answers: QuizAnswers,
  options: BuildProfileOptions = {},
): UserProfileInput | null {
  const name = asString(answers.name) ?? asString(options.fallbackName) ?? FALLBACK_NAME
  const gender = asString(answers.gender)
  const birthDate = asString(answers.dob)

  if (!gender || !GENDERS.includes(gender as Gender) || !birthDate) return null

  const year = Number(birthDate.split("-")[0])
  const zodiacSign = getZodiacSlug(birthDate)
  if (!Number.isInteger(year) || !zodiacSign) return null

  const marital = asString(answers.marital)

  return {
    name,
    gender: gender as Gender,
    birthDate,
    birthTime: normalizeBirthTime(asString(answers.tob)),
    year,
    zodiacSign,
    maritalStatus:
      marital && MARITAL_STATUSES.includes(marital as MaritalStatus)
        ? (marital as MaritalStatus)
        : null,
    contactEmail: asString(answers.email),
    birthPlace: parseBirthPlace(asString(answers.birthplace)),
  }
}
